import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FlaskConical, Award, Briefcase, Bot, RefreshCw, Ban, ExternalLink,
} from 'lucide-react';
import { AdminApi, CareerApi } from '../../services/api';
import './QoreTalksAdmin.css';

const Header = ({ icon: Icon, title, subtitle, children }) => (
  <div className="qt-admin__header">
    <div>
      <h2><Icon size={22} /> {title}</h2>
      <p>{subtitle}</p>
    </div>
    {children && <div className="qt-admin__actions">{children}</div>}
  </div>
);

const Stats = ({ items }) => (
  <div className="qt-stats">
    {items.map((s) => (
      <div key={s.label} className="qt-stat">
        <div className="qt-stat__icon" style={{ background: s.color }}>{s.icon}</div>
        <div>
          <p className="qt-stat__value">{s.value}</p>
          <p className="qt-stat__label">{s.label}</p>
        </div>
      </div>
    ))}
  </div>
);

export const AcademyLabsPanel = ({ token, onNotify }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await AdminApi.getAcademyLabs(token);
      setData(res.success ? res.data : null);
    } catch (e) {
      onNotify?.('error', 'Labs', e.message);
    } finally {
      setLoading(false);
    }
  }, [token, onNotify]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="qt-admin">
      <Header icon={FlaskConical} title="Virtual labs" subtitle="Catalog health for medical stats, physics, and engineering labs.">
        <button type="button" className="qt-btn qt-btn--ghost" onClick={load}><RefreshCw size={15} /> Refresh</button>
        <Link to="/virtuallabs" className="qt-btn qt-btn--primary" target="_blank" rel="noreferrer">
          Open labs <ExternalLink size={14} />
        </Link>
      </Header>
      {loading ? <div className="loading-state"><RefreshCw className="spin" size={24} /></div> : (
        <>
          <Stats items={[
            { icon: <FlaskConical size={18} />, value: data?.stats?.total || 0, label: 'Labs', color: '#22d3ee' },
            { icon: <Award size={18} />, value: data?.stats?.keysIssued || 0, label: 'Keys issued', color: '#f59e0b' },
          ]} />
          <div className="qt-subscribers-table">
            {(data?.labs || []).map((lab) => (
              <div key={lab._id || lab.labId} className="qt-sub-row">
                <div className="qt-sub-avatar">{(lab.title || 'L').charAt(0)}</div>
                <div>
                  <p className="qt-sub-email">{lab.title}</p>
                  <p className="qt-sub-date">{lab.category} · {lab.status || 'active'} · {lab.labId}</p>
                </div>
              </div>
            ))}
            {!(data?.labs || []).length && <p className="empty-state">No labs seeded yet.</p>}
          </div>
        </>
      )}
    </div>
  );
};

export const AcademyCertificatesPanel = ({ token, onNotify }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await AdminApi.getAcademyCertificates(token, { limit: 30 });
      setData(res.success ? res.data : null);
    } catch (e) {
      onNotify?.('error', 'Certificates', e.message);
    } finally {
      setLoading(false);
    }
  }, [token, onNotify]);

  useEffect(() => { load(); }, [load]);

  const revoke = async (id) => {
    const reason = window.prompt('Revoke reason?') || 'Revoked by admin';
    setBusy(id);
    try {
      const res = await AdminApi.revokeCertificate(token, id, reason);
      onNotify?.(res.success ? 'success' : 'error', 'Certificate', res.message);
      await load();
    } catch (e) {
      onNotify?.('error', 'Certificate', e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="qt-admin">
      <Header icon={Award} title="Certificates" subtitle="Issued QoreAcademy credentials. Revoke is a high-stakes Nova tool.">
        <button type="button" className="qt-btn qt-btn--ghost" onClick={load}><RefreshCw size={15} /> Refresh</button>
      </Header>
      {loading ? <div className="loading-state"><RefreshCw className="spin" size={24} /></div> : (
        <>
          <Stats items={[
            { icon: <Award size={18} />, value: data?.stats?.active || 0, label: 'Active', color: '#34d399' },
            { icon: <Ban size={18} />, value: data?.stats?.revoked || 0, label: 'Revoked', color: '#fb7185' },
          ]} />
          <div className="qt-subscribers-table">
            {(data?.certificates || []).map((c) => (
              <div key={c._id} className="qt-sub-row">
                <div className="qt-sub-avatar">C</div>
                <div style={{ flex: 1 }}>
                  <p className="qt-sub-email">{c.courseName || c.course?.title}</p>
                  <p className="qt-sub-date">
                    {c.user?.email || c.user?.firstName} · {c.certificateId} · {c.status}
                  </p>
                </div>
                {c.status !== 'revoked' && (
                  <button
                    type="button"
                    className="qt-btn qt-btn--danger qt-btn--sm"
                    disabled={busy === c._id}
                    onClick={() => revoke(c._id)}
                  >
                    <Ban size={14} /> Revoke
                  </button>
                )}
              </div>
            ))}
            {!(data?.certificates || []).length && <p className="empty-state">No certificates issued yet.</p>}
          </div>
        </>
      )}
    </div>
  );
};

export const AcademyCareerPanel = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    CareerApi.getMarketStats()
      .then((res) => setStats(res?.data || res))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="qt-admin">
      <Header icon={Briefcase} title="Career intelligence" subtitle="Skill-to-job market feed used by QoreAcademy paths and QoreConnect jobs.">
        <Link to="/careerpaths" className="qt-btn qt-btn--primary" target="_blank" rel="noreferrer">
          Career paths <ExternalLink size={14} />
        </Link>
      </Header>
      {loading ? <div className="loading-state"><RefreshCw className="spin" size={24} /></div> : (
        <Stats items={[
          { icon: <Briefcase size={18} />, value: stats?.totalJobs ?? '—', label: 'Jobs tracked', color: '#10b981' },
          { icon: <Award size={18} />, value: stats?.skillsTracked ?? stats?.emergingSkills ?? '—', label: 'Skills signal', color: '#8b5cf6' },
        ]} />
      )}
    </div>
  );
};

export const AcademyTutorPanel = ({ token, onNotify }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await AdminApi.getAcademyTutor(token);
      setData(res.success ? res.data : null);
    } catch (e) {
      onNotify?.('error', 'Tutor', e.message);
    } finally {
      setLoading(false);
    }
  }, [token, onNotify]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="qt-admin">
      <Header icon={Bot} title="AI tutor ops" subtitle="Conversation volume across the QoreAcademy tutor.">
        <button type="button" className="qt-btn qt-btn--ghost" onClick={load}><RefreshCw size={15} /> Refresh</button>
      </Header>
      {loading ? <div className="loading-state"><RefreshCw className="spin" size={24} /></div> : (
        <>
          <Stats items={[
            { icon: <Bot size={18} />, value: data?.stats?.total || 0, label: 'Conversations', color: '#8b5cf6' },
            { icon: <RefreshCw size={18} />, value: data?.stats?.last24h || 0, label: 'Last 24h', color: '#22d3ee' },
          ]} />
          <div className="qt-subscribers-table">
            {(data?.recent || []).map((c) => (
              <div key={c.id} className="qt-sub-row">
                <div className="qt-sub-avatar">AI</div>
                <div>
                  <p className="qt-sub-email">{c.title || 'Conversation'}</p>
                  <p className="qt-sub-date">
                    {c.user?.email || c.user?.firstName || 'Learner'} · {c.messages} msgs · {c.page || 'app'}
                  </p>
                </div>
              </div>
            ))}
            {!(data?.recent || []).length && <p className="empty-state">No tutor conversations yet.</p>}
          </div>
        </>
      )}
    </div>
  );
};
